import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import {
  getCurrentSalesDocument as getCurrentPaymentSalesDocument,
  getBalance,
  getIsEligibleForCashRounding,
  getChange,
  getIsReturnPayment,
} from 'reducers/Payments';
import { reactcursively } from 'utils/react';
import { ErplyAttributes } from 'utils';
import { useShortcut } from 'utils/hooks/keyboard/useShortcut';

import {
  CURBSIDE_PAYMENT_REQUIRED,
  INSTORE_PAYMENT_REQUIRED,
} from './constants';

const paymentConfirmationButton = 'confirm-payment-button';

const WithPayButtonDisabledOnPaymentRequired: Required<
  PosPlugin
>['UIPaymentsModalPayButton'] = ({ children, confirmHandler, ...rest }) => {
  const { attributes, deliveryTypeName } = useSelector(
    getCurrentPaymentSalesDocument,
  );
  const balance = useSelector(getBalance);
  const attributesAsDict = new ErplyAttributes(attributes);
  const isEligibleForCashRounding = useSelector(getIsEligibleForCashRounding);
  const isReturnPayment = useSelector(getIsReturnPayment);
  const change = useSelector(getChange);

  /**
   * Copied and slightly edited from {@link ./src/containers/Forms/Payment/components/PaymentActions/components/ConfirmPayment/index.tsx shouldConfirmBeDisabled}.
   */
  const shouldConfirmBeDisabled = useMemo(() => {
    // If eligible for rounding, then change not being negative means the customer is close enough to the total
    // otherwise, they must reach the total perfectly so we check the unrounded balance instead
    // i.e.
    //   Total | Payments | Allow
    //   5.25    4.00       Block regardless because balance is -1.25 and change is -1.00
    //   5.25    5.00       Allow if eligible for rounding, because change is -0.00
    //   5.25    6.00       Allow always because customer has overpaid
    const compare = isEligibleForCashRounding ? change : balance;
    return isReturnPayment
      ? 0 < compare // For returns, underpayment is when change/balance is positive
      : compare < 0; // For sales, underpayment is when change/balance is negative
  }, [isEligibleForCashRounding, isReturnPayment, balance, change]);

  const savedDocIsOnlineOrder =
    !!attributesAsDict.get('pickupStatus') ||
    !!attributesAsDict.get('pickupProcessed') ||
    !!attributesAsDict.get('ConfirmInvoice');

  const documentRequiresPayment = [
    CURBSIDE_PAYMENT_REQUIRED,
    INSTORE_PAYMENT_REQUIRED,
  ].includes(deliveryTypeName);

  const disabled =
    rest.disabled ||
    (savedDocIsOnlineOrder &&
      documentRequiresPayment &&
      shouldConfirmBeDisabled);

  const newHandler = disabled
    ? () => {
        // Do nothing
      }
    : confirmHandler;

  useShortcut('F8', newHandler, 21);

  return R.pipe(
    reactcursively(
      R.when(el => {
        const equals =
          (el?.props?.['data-testid'] ?? '') === paymentConfirmationButton;
        return equals;
      }, R.assocPath(['props', 'disabled'], disabled)),
    ),
  )(React.Children.toArray(children));
};

export default WithPayButtonDisabledOnPaymentRequired;
